/**
 * Lightweight notification chime using the Web Audio API.
 *
 * Why Web Audio rather than an <audio> element?
 *  - Zero asset weight (no .mp3 / .ogg shipped).
 *  - Crisp and identical on every device — no codec drift.
 *  - One AudioContext, lazy-initialised on the first user gesture
 *    (required by Safari / Chrome autoplay policies).
 *
 * The "chime" is a two-note (G5 → C6) refined bell with a soft
 * exponential decay envelope, matching the editorial / luxury tone of the app.
 *
 * Quiet hours: by default the chime is suppressed between 23:45 and 08:00
 * (local device time) — high-end members tend to silence late-night nudges.
 * The window is overridable via localStorage:
 *   lumiere_dnd_start_min  (minutes since midnight, default 1425 = 23:45)
 *   lumiere_dnd_end_min    (minutes since midnight, default 480  = 08:00)
 *   lumiere_dnd_disabled   ("1" to bypass quiet hours entirely)
 */
let _ctx = null;
let _muted = false;
const MUTE_KEY = "lumiere_notif_muted";
const DND_START_KEY = "lumiere_dnd_start_min";
const DND_END_KEY = "lumiere_dnd_end_min";
const DND_DISABLED_KEY = "lumiere_dnd_disabled";
const DEFAULT_DND_START = 23 * 60 + 45; // 23:45
const DEFAULT_DND_END = 8 * 60; // 08:00

try {
  _muted = localStorage.getItem(MUTE_KEY) === "1";
} catch {
  /* ignore */
}

function readInt(key, fallback) {
  try {
    const v = parseInt(localStorage.getItem(key) || "", 10);
    return Number.isFinite(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function isQuietHour(now = new Date()) {
  try {
    if (localStorage.getItem(DND_DISABLED_KEY) === "1") return false;
  } catch {
    /* ignore */
  }
  const start = readInt(DND_START_KEY, DEFAULT_DND_START);
  const end = readInt(DND_END_KEY, DEFAULT_DND_END);
  const cur = now.getHours() * 60 + now.getMinutes();
  // Window crosses midnight when start > end (e.g. 23:45 → 08:00)
  return start <= end ? cur >= start && cur < end : cur >= start || cur < end;
}

export { isQuietHour };

function getCtx() {
  if (_ctx) return _ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    _ctx = new Ctor();
  } catch {
    return null;
  }
  return _ctx;
}

/** Must be called from inside a user-gesture handler (e.g. click on bell). */
export function unlockAudio() {
  const ctx = getCtx();
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
}

export function isMuted() {
  return _muted;
}

export function setMuted(v) {
  _muted = !!v;
  try {
    localStorage.setItem(MUTE_KEY, _muted ? "1" : "0");
  } catch {
    /* ignore */
  }
}

function tone(ctx, freq, startAt, duration, gain) {
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, startAt);
  env.gain.setValueAtTime(0, startAt);
  env.gain.linearRampToValueAtTime(gain, startAt + 0.02);
  env.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  osc.connect(env).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.05);
}

export function playChime() {
  if (_muted) return;
  if (isQuietHour()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    // Best-effort: if Safari hasn't unlocked yet, attempt resume but don't error
    ctx.resume().catch(() => {});
  }
  const t0 = ctx.currentTime + 0.01;
  // G5 → C6 short refined chime
  tone(ctx, 783.99, t0, 0.45, 0.18);
  tone(ctx, 1046.5, t0 + 0.12, 0.55, 0.14);
}
